import { allowedCountries } from "../mocks/mockData.js";

//const LANDSTAR_API_BASE_URL = "http://localhost:4000/api/landstar";
const LANDSTAR_API_BASE_URL = "https://ofm.stran.store/api/landstar";
const LANDSTAR_SUBMISSION_DRY_RUN = false;

export class LandstarApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "LandstarApiError";
    this.order = options.order || null;
  }
}

export async function validateDriverCode(driverCode, email) {
  try {
    const response = await fetch(`${LANDSTAR_API_BASE_URL}/validate-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        driverCode,
        email
      })
    });

    const json = await parseApiResponse(response);

    if (!response.ok) {
      throw new LandstarApiError(json?.error || "Unable to validate driver code.", {
        order: json?.order || null
      });
    }

    return json.data;
  } catch (error) {
    if (error instanceof LandstarApiError) {
      throw error;
    }

    throw new LandstarApiError(error.message || "Unable to validate driver code.");
  }
}

export async function getEligibleRewards(driverProfile) {
  try {
    const response = await fetch(`${LANDSTAR_API_BASE_URL}/products`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        driverCode: driverProfile?.driverCode,
        lNumber: driverProfile?.lNumber,
        rewardGroup: driverProfile?.rewardGroup,
        qualifiedYears: driverProfile?.qualifiedYears
      })
    });

    const json = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(json?.error || "Unable to load eligible rewards.");
    }

    const products = Array.isArray(json.data) ? json.data : [];

    if (!products.length && driverProfile?.rewardGroup !== "manual") {
      throw new Error("You're not eligible for rewards at this time. Please contact support.");
    }

    return products;
  } catch (error) {
    throw new Error(error.message || "Unable to load eligible rewards.");
  }
}

export async function submitRedemption(payload) {
  try {
    if (LANDSTAR_SUBMISSION_DRY_RUN) {
      console.group("Landstar redemption payload preview");
      console.log(payload);
      console.groupEnd();

      return {
        success: true,
        confirmationId: Date.now().toString().slice(-10).padStart(10, "0"),
        stageStatus: "mocked",
        payload
      };
    }

    const response = await fetch(`${LANDSTAR_API_BASE_URL}/stage-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    const json = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(json?.error || "Unable to submit redemption.");
    }

    return json;
  } catch (error) {
    throw new Error(error.message || "Unable to submit redemption.");
  }
}

export async function getLandstarOrderStatus(submissionId) {
  try {
    const response = await fetch(
      `${LANDSTAR_API_BASE_URL}/order-status/${encodeURIComponent(submissionId)}`
    );
    const json = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(json?.error || "Unable to load order status.");
    }

    return json.data;
  } catch (error) {
    throw new Error(error.message || "Unable to load order status.");
  }
}

export async function importLandstarEligibilitySpreadsheet(file, access) {
  try {
    const fileContentBase64 = await readFileAsBase64(file);
    const response = await fetch(`${LANDSTAR_API_BASE_URL}/import-eligibility`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        driverCode: access?.driverCode,
        email: access?.email,
        fileName: file.name,
        fileContentBase64
      })
    });

    const json = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(json?.error || "Unable to import the spreadsheet.");
    }

    return json;
  } catch (error) {
    throw new Error(error.message || "Unable to import the spreadsheet.");
  }
}

export function getAllowedCountries() {
  return allowedCountries;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.replace(/^data:.*;base64,/, ""));
    };

    reader.onerror = () => {
      reject(new Error("Unable to read the spreadsheet file."));
    };

    reader.readAsDataURL(file);
  });
}

async function parseApiResponse(response) {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    const trimmedText = text.trim();

    if (trimmedText.startsWith("<!DOCTYPE") || trimmedText.startsWith("<html")) {
      return {
        error: response.status === 404
          ? "This Landstar backend route is not available in the current environment yet."
          : "The Landstar backend returned an unexpected HTML response."
      };
    }

    return {
      error: trimmedText
    };
  }
}
